import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Doughnut } from 'react-chartjs-2';
import MangaApiService from '../../services/MangaApiService';
import statsIcon from '../../assets/stats.svg';
import teamIcon from '../../assets/ic_profile.svg';

// Same order and colours as the anime chart: done, dropped, on hold, in progress, planned.
export const MANGA_STATUSES = [
    { key: 'COMPLETED', label: 'Прочитано', color: '#79E936' },
    { key: 'DROPPED', label: 'Брошено', color: '#EB5744' },
    { key: 'ON_HOLD', label: 'На паузе', color: '#FDCF2A' },
    { key: 'READING', label: 'Читаю', color: '#929292' },
    { key: 'PLAN_TO_READ', label: 'В планах', color: '#36B69C' },
];

const CHART_OPTIONS = {
    responsive: true,
    borderWidth: 0,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
};

const plural = (count, forms) => {
    const mod10 = count % 10;
    const mod100 = count % 100;
    if (mod10 === 1 && mod100 !== 11) return forms[0];
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1];
    return forms[2];
};

/** Manga side of a profile: list counts, review count and team. Null until loaded or when empty. */
export const useMangaProfile = (userId) => {
    const [profile, setProfile] = useState(null);

    useEffect(() => {
        let active = true;
        setProfile(null);
        if (!userId) return undefined;
        MangaApiService.getUserMangaProfile(userId)
            .then((data) => active && setProfile(data))
            .catch(() => active && setProfile(null));
        return () => {
            active = false;
        };
    }, [userId]);

    if (!profile) return null;
    const lists = profile.lists || {};
    const total = Number(profile.listTotal) || 0;
    const reviews = Number(profile.reviewsCount) || 0;
    const team = profile.team || null;
    if (total === 0 && reviews === 0 && !team) return null;
    return { total, reviews, team, lists };
};

const TeamLogo = ({ team }) => {
    const [url, setUrl] = useState(null);

    useEffect(() => {
        let active = true;
        if (!team?.logoFileId || !team?.logoStorageBucket) {
            setUrl(null);
            return undefined;
        }
        const file = { fileId: team.logoFileId, bucket: team.logoStorageBucket };
        const direct = MangaApiService.resolveFileUrl(file, team.logoStorageBucket);
        if (direct) {
            setUrl(direct);
            return undefined;
        }
        MangaApiService.resolveFileUrlAsync(file, team.logoStorageBucket)
            .then((resolved) => active && setUrl(resolved || null))
            .catch(() => active && setUrl(null));
        return () => {
            active = false;
        };
    }, [team?.logoFileId, team?.logoStorageBucket]);

    return <img src={url || teamIcon} alt="" className={url ? '' : 'is-placeholder'} />;
};

TeamLogo.propTypes = {
    team: PropTypes.object.isRequired,
};

/**
 * One card for both collections: a donut with counts, switched between anime and manga.
 * `onOpen(kind)` opens the lists in the profile's activity panel instead of leaving the page.
 */
const ProfileCollectionsCard = ({ animeData, manga, onOpen }) => {
    const [current, setKind] = useState('anime');
    const mangaLists = manga?.lists || {};
    const mangaTotal = manga?.total || 0;

    // Both sides are always switchable; a side with nothing in it shows a note instead of the chart.
    const chart = current === 'manga'
        ? (mangaTotal > 0 ? {
            labels: MANGA_STATUSES.map((status) => status.label),
            datasets: [{
                data: MANGA_STATUSES.map((status) => Number(mangaLists[status.key]) || 0),
                backgroundColor: MANGA_STATUSES.map((status) => status.color),
            }],
        } : null)
        : animeData;

    const total = chart ? chart.datasets[0].data.reduce((sum, n) => sum + (Number(n) || 0), 0) : 0;
    const subtitle = current === 'manga'
        ? `${total} ${plural(total, ['тайтл', 'тайтла', 'тайтлов'])}${manga?.reviews > 0 ? ` · ${manga.reviews} ${plural(manga.reviews, ['отзыв', 'отзыва', 'отзывов'])}` : ''}`
        : `${total} ${plural(total, ['аниме', 'аниме', 'аниме'])}`;

    return (
        <section className="profile-card profile-collections-card">
            <div className="profile-card__header">
                <span className="profile-card__icon"><img src={statsIcon} alt="" /></span>
                <div>
                    <h2>Коллекции</h2>
                    <p>{subtitle}</p>
                </div>
            </div>
            <div className="profile-seg profile-seg--row" role="tablist" aria-label="Тип коллекции">
                <button type="button" role="tab" aria-selected={current === 'anime'} onClick={() => setKind('anime')}>Аниме</button>
                <button type="button" role="tab" aria-selected={current === 'manga'} onClick={() => setKind('manga')}>Манга</button>
            </div>

            {chart ? (
                <div className="profile-donut">
                    <div className="profile-donut__chart"><Doughnut data={chart} options={CHART_OPTIONS} /></div>
                    <ul className="profile-donut__legend">
                        {chart.labels.map((label, index) => (
                            <li key={label}>
                                <i style={{ backgroundColor: chart.datasets[0].backgroundColor[index] }} />
                                <span>{label}</span>
                                <strong>{chart.datasets[0].data[index] || 0}</strong>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : (
                <div className="profile-empty-state is-small">
                    <p>{current === 'manga' ? 'Списков манги пока нет.' : 'Коллекций аниме пока нет.'}</p>
                </div>
            )}

            {manga?.team ? (
                <Link className="profile-manga-team" to={`/manga/teams/${manga.team.id}`}>
                    <span className="profile-manga-team__logo"><TeamLogo team={manga.team} /></span>
                    <span className="profile-manga-team__text">
                        <strong>{manga.team.name}</strong>
                        <span>{manga.team.role === 'OWNER' ? 'Владелец команды манги' : 'Участник команды манги'}</span>
                    </span>
                    <span className="profile-manga-team__arrow" aria-hidden="true">→</span>
                </Link>
            ) : null}

            <button type="button" className="profile-card__more" onClick={() => onOpen(current)}>
                {current === 'manga' ? 'Смотреть списки манги' : 'Смотреть коллекции'}
            </button>
        </section>
    );
};

ProfileCollectionsCard.propTypes = {
    animeData: PropTypes.object,
    manga: PropTypes.object,
    onOpen: PropTypes.func.isRequired,
};

ProfileCollectionsCard.defaultProps = {
    animeData: null,
    manga: null,
};

export default ProfileCollectionsCard;
